import React, { useState, useEffect } from "react";
import styles from "./ProductCard.module.css";
import { useNavigate } from "react-router-dom";
import EnquireModal from "../EnquiryForm";
import {
  incrementProductViewCount,
  likeProduct,
  unLikeProduct,
} from "../../service/product";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartPlus,
  faShoppingCart,
  faEye,
  faHeart,
  faShippingFast,
  faShieldAlt,
  faStar,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useCart } from "../../context/CartContext";
import CartSidebar from "../CartSidebar";
import { Modal, ModalBody } from "react-bootstrap";
import LoginModal from "../login";
import QuickViewModal from "../QuickViewModal";

const ProductCard = ({ product, series }: any) => {
  const [showEnquireModal, setShowEnquireModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user } = useAuth();
  const [showQuickView, setShowQuickView] = useState(false);
  const discount =
    product.price > 0
      ? Math.round(((product.price - product.ourPrice) / product.price) * 100)
      : 0;

  let allImages: any = product.picture.map((i: any) => i.path);
  // Auto-play effect when hovering
  useEffect(() => {
    if (!isHovering || allImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovering, allImages.length]);

  const handleEnquireClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEnquireModal(true);
  };

  const addViewCount = async (productId: any) => {
    await incrementProductViewCount(productId);
  };

  const { addToCart, cart, setCart, removeFromCart } = useCart();

  const handleAddToCart = (product: any) => {
    addToCart({ ...product, quantity: 1 });
    setIsCartOpen(() => true);
  };

  const handleUpdateQuantity = (id: string, quantity: number) => {
    setCart(
      cart.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const isInCart = cart?.some(
    (p: any) => p._id?.toString() === product?._id?.toString()
  );

  const navigateToProduct = () => {
    navigate(`/series/${series.id}/product/${product._id}`);
    addViewCount(product._id);
  };

  const handleImageNavigation = (
    direction: "prev" | "next",
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (direction === "prev") {
      setCurrentImageIndex((prev) =>
        prev === 0 ? allImages.length - 1 : prev - 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }
  };
  const [isLiked, setIsLiked] = useState(product?.hasUpvoted);

  const toggleLikeProduct = async (productId: any) => {
    try {
      await likeProduct(productId);
      setIsLiked(true);
    } catch (error) {
      console.log("error in liking the product", error);
    }
  };
  const toggleUnlikeProduct = async (productId: any) => {
    try {
      await unLikeProduct(productId);
      setIsLiked(false);
    } catch (error) {
      console.log("error in unliking the product", error);
    }
  };

  const handleQuickView = (e: any) => {
    e.stopPropagation();
    setShowQuickView(true);
  };

  // Calculate average rating (mock data for now)
  const averageRating = product.rating || 4.5;
  const reviewCount = product.reviewCount || 12;

  return (
    <>
      <motion.div
        className={styles.productCardWrapper}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -8 }}
      >
        <div className={styles.productCardLink}>
          <div className={styles.productCard}>
            {/* Image Container with Enhanced Features */}
            <div className={styles.imageContainer}>
              <div className={styles.imageCarousel}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={allImages[currentImageIndex]}
                    alt={`${series.title} - ${product.name}`}
                    className={styles.productImage}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <button
                      className={`${styles.navArrow} ${styles.prevArrow}`}
                      onClick={(e) => handleImageNavigation("prev", e)}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className={`${styles.navArrow} ${styles.nextArrow}`}
                      onClick={(e) => handleImageNavigation("next", e)}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </>
                )}

                {/* Top Badges */}
                <div className={styles.topBadges}>
                  {discount > 0 && (
                    <div className={styles.discountBadge}>{discount}% OFF</div>
                  )}
                  {product.isNew && <div className={styles.newBadge}>New</div>}
                  {product.bestSeller && (
                    <div className={styles.bestSellerBadge}>Best Seller</div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className={styles.quickActions}>
                  <motion.button
                    className={styles.quickActionBtn}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Wishlist"
                    onClick={async () =>
                      !user
                        ? setShowLoginModal(true)
                        : isLiked
                        ? toggleUnlikeProduct(product?._id)
                        : toggleLikeProduct(product?._id)
                    }
                  >
                    <FontAwesomeIcon
                      icon={faHeart}
                      className={isLiked ? styles.wishlisted : ""}
                    />
                  </motion.button>
                  <motion.button
                    className={styles.quickActionBtn}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleQuickView}
                    title="Quick View"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </motion.button>
                </div>
              </div>

              {/* Carousel Indicators */}
              {allImages.length > 1 && (
                <div className={styles.carouselIndicators}>
                  {allImages.map((_: any, index: any) => (
                    <button
                      key={index}
                      className={`${styles.indicator} ${
                        index === currentImageIndex
                          ? styles.activeIndicator
                          : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className={styles.productInfo}>
              {/* Category Tag */}
              <div className={styles.categoryTag}>{series.title}</div>

              {/* Product Name */}
              <h3 className={styles.productName} onClick={navigateToProduct}>
                {product.name}
              </h3>

              {/* Rating */}
              <div className={styles.ratingSection}>
                <div className={styles.stars}>
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon
                      key={i}
                      icon={faStar}
                      className={
                        i < Math.floor(averageRating)
                          ? styles.filledStar
                          : styles.emptyStar
                      }
                    />
                  ))}
                </div>
                <span className={styles.ratingText}>
                  ({averageRating}) • {reviewCount} reviews
                </span>
              </div>

              {/* Features */}
              <div className={styles.features}>
                <div className={styles.feature}>
                  <FontAwesomeIcon icon={faShippingFast} />
                  <span>Free Shipping</span>
                </div>
                <div className={styles.feature}>
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <span>2 Year Warranty</span>
                </div>
              </div>

              {/* Pricing */}
              <div className={styles.pricing}>
                {discount > 0 ? (
                  <>
                    <span className={styles.originalPrice}>
                      ₹{product.price.toLocaleString()}
                    </span>
                    <div className={styles.discountPricing}>
                      <span className={styles.discountedPrice}>
                        ₹{product.ourPrice.toLocaleString()}
                      </span>
                      <span className={styles.priceUnit}>/piece</span>
                    </div>
                  </>
                ) : (
                  <div className={styles.regularPricing}>
                    <span className={styles.regularPrice}>
                      ₹{product.ourPrice.toLocaleString()}
                    </span>
                    <span className={styles.priceUnit}>/piece</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <motion.button
                  className={styles.enquireButton}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleEnquireClick}
                >
                  Enquire Now
                </motion.button>
                <motion.button
                  className={`${styles.addToCartBtn} ${
                    isInCart ? styles.inCart : ""
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    isInCart
                      ? removeFromCart(product?._id)
                      : handleAddToCart(product);
                  }}
                >
                  <FontAwesomeIcon
                    icon={isInCart ? faShoppingCart : faCartPlus}
                  />
                  {isInCart ? "Remove from Cart" : "Add to Cart"}
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showEnquireModal && (
          <EnquireModal
            show={showEnquireModal}
            onHide={() => setShowEnquireModal(false)}
            product={product}
            series={series}
          />
        )}
      </motion.div>

      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
      />
      {showLoginModal && (
        <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
          <ModalBody style={{ padding: "0px", margin: "0px" }}>
            <LoginModal isStandalone={true} />
          </ModalBody>
        </Modal>
      )}
      <QuickViewModal
        product={product}
        series={series}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
        onAddToCart={handleAddToCart}
      />
    </>
  );
};

export default ProductCard;
